import { useCache } from '~/common/useCache'

export function useAnalytics() {
  const { gtag } = useGtag()
  const router = useRouter()
  const app = useAppStore()

  const trackEvent = (eventName: string, params: Record<string, any> = {}) => {
    gtag('event', eventName, params)
  }

  const trackButtonClick = (
    eventName: string,
    buttonName: string,
    params: Record<string, any> = {},
    category?: string,
  ) => {
    trackEvent(eventName, {
      button_name: buttonName,
      event_category: category || 'button_click',
      ...params,
    })
  }

  const trackPageView = (to: any) => {
    trackEvent('page_view', {
      page_title: document.title,
      page_path: to.path,
      page_full_path: to.fullPath,
      timestamp: new Date().toISOString(),
      um_idx: app.user ? app.user.UM_IDX : '',
    })
  }

  // 새로운 자동 버튼 클릭 추적 함수
  const setupAutoButtonTracking = () => {
    // 클릭 이벤트 위임을 통한 처리
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const clickedElement = target.closest(
        'button, a, [class*="btn"], [class*="Btn"], [role="button"], [class*="button"]',
      )

      if (!clickedElement) return

      // 결제, 사용자 관련 버튼은 제외 (이미 수동으로 추적 중)
      const excludePatterns = [
        'payment',
        'pay',
        'login',
        'logout',
        'signup',
        'user',
        'error',
      ]

      const elementClasses = clickedElement.className || ''
      const elementId = clickedElement.id || ''
      if (
        excludePatterns.some(
          (pattern) =>
            elementClasses.toLowerCase().includes(pattern) ||
            elementId.toLowerCase().includes(pattern),
        )
      ) {
        return
      }

      // 버튼 정보 수집
      const buttonText = clickedElement.textContent?.trim() || '버튼'
      const params = {
        button_name: buttonText,
        event_category: 'button_click',
        page_path: router.currentRoute.value.path,
        element_type: clickedElement.tagName.toLowerCase(),
        element_classes: elementClasses,
        element_id: elementId,
        timestamp: new Date().toISOString(),
        um_idx: app.user ? app.user.UM_IDX : '',
      }

      trackEvent('auto_button_click', params)
    })
  }

  // 자동 이벤트 추적 설정 확장
  const setupAutoTracking = () => {
    // 페이지 변경 추적
    router.afterEach((to) => {
      // 페이지 뷰 이벤트에 추가 정보 포함
      trackPageView({
        ...to,
        referrer: document.referrer,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString(),
        um_idx: app.user ? app.user.UM_IDX : '',
      })
    })

    // 자동 버튼 클릭 추적 설정
    onMounted(() => {
      if (app.user !== null) {
        setupAutoButtonTracking()
      }
    })
  }

  // 페이지 체류 시간 추적
  const setupPageTimeTracking = () => {
    let pageEnterTime = Date.now()
    let isPageVisible = true
    let totalActiveTime = 0
    let lastVisibilityChange = pageEnterTime

    const trackPageTime = (eventType: 'page_exit' | 'visibility_change') => {
      const now = Date.now()
      const totalTime = now - pageEnterTime

      if (isPageVisible) {
        totalActiveTime += now - lastVisibilityChange
      }

      trackEvent('page_time_tracking', {
        event_category: 'engagement',
        event_type: eventType,
        total_time_ms: totalTime,
        active_time_ms: totalActiveTime,
        page_path: router.currentRoute.value.path,
        engagement_rate:
          totalTime > 0 ? Math.round((totalActiveTime / totalTime) * 100) : 0,
        um_idx: app.user ? app.user.UM_IDX : '',
        timestamp: new Date().toISOString(),
      })
    }

    const handleVisibilityChange = () => {
      const now = Date.now()

      if (document.visibilityState === 'visible') {
        if (!isPageVisible) {
          isPageVisible = true
          lastVisibilityChange = now
        }
      } else {
        if (isPageVisible) {
          totalActiveTime += now - lastVisibilityChange
          isPageVisible = false
          trackPageTime('visibility_change')
        }
      }
    }

    // 라우터 변경 전 체류시간 추적
    const handleRouteChange = () => {
      if (isPageVisible) {
        totalActiveTime += Date.now() - lastVisibilityChange
      }
      trackPageTime('page_exit')
    }

    onMounted(() => {
      pageEnterTime = Date.now()
      lastVisibilityChange = pageEnterTime
      document.addEventListener('visibilitychange', handleVisibilityChange)
      router.beforeEach(handleRouteChange)
    })

    onUnmounted(() => {
      if (isPageVisible) {
        totalActiveTime += Date.now() - lastVisibilityChange
      }
      trackPageTime('page_exit')
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    })

    return { trackPageTime }
  }

  // 기본 스크롤 깊이 추적
  const setupScrollTracking = () => {
    const scrollMilestones = [25, 50, 75, 100]
    const reachedMilestones = new Set<number>()
    let maxScrollPercentage = 0
    let scrollStartTime = Date.now()

    const calculateScrollPercentage = (): number => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const documentHeight =
        document.documentElement.scrollHeight - window.innerHeight
      return documentHeight > 0
        ? Math.round((scrollTop / documentHeight) * 100)
        : 100
    }

    const handleScroll = () => {
      const scrollPercentage = calculateScrollPercentage()
      maxScrollPercentage = Math.max(maxScrollPercentage, scrollPercentage)

      // 마일스톤 체크
      scrollMilestones.forEach((milestone) => {
        if (
          scrollPercentage >= milestone &&
          !reachedMilestones.has(milestone)
        ) {
          reachedMilestones.add(milestone)

          trackEvent('scroll_milestone', {
            event_category: 'engagement',
            milestone_percentage: milestone,
            time_to_milestone: Date.now() - scrollStartTime,
            page_path: router.currentRoute.value.path,
            um_idx: app.user ? app.user.UM_IDX : '',
            timestamp: new Date().toISOString(),
          })
        }
      })
    }

    const trackScrollSummary = () => {
      if (maxScrollPercentage > 0) {
        trackEvent('scroll_session_summary', {
          event_category: 'engagement',
          max_scroll_percentage: maxScrollPercentage,
          session_duration: Date.now() - scrollStartTime,
          page_path: router.currentRoute.value.path,
          um_idx: app.user ? app.user.UM_IDX : '',
          timestamp: new Date().toISOString(),
        })
      }
    }

    let scrollTimeout: number
    const throttledScroll = () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(handleScroll, 250) // 250ms 스로틀링
    }

    onMounted(() => {
      scrollStartTime = Date.now()
      window.addEventListener('scroll', throttledScroll, { passive: true })
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', throttledScroll)
      trackScrollSummary()
    })

    return { maxScrollPercentage, reachedMilestones }
  }

  // 세션 품질 측정
  const setupSessionAnalytics = () => {
    const sessionData = {
      startTime: Date.now(),
      pageViews: 0,
      interactions: 0,
      bounceThreshold: 10000, // 10초
    }

    const calculateSessionQuality = () => {
      const sessionDuration = Date.now() - sessionData.startTime
      const avgTimePerPage =
        sessionData.pageViews > 0 ? sessionDuration / sessionData.pageViews : 0
      const interactionRate =
        sessionDuration > 0
          ? sessionData.interactions / (sessionDuration / 1000)
          : 0

      // 세션 품질 점수 계산 (0-100)
      let qualityScore = 0

      // 시간 기반 점수 (40%)
      if (sessionDuration > 300000)
        qualityScore += 40 // 5분 이상
      else if (sessionDuration > 120000)
        qualityScore += 30 // 2분 이상
      else if (sessionDuration > 30000)
        qualityScore += 20 // 30초 이상
      else qualityScore += 10

      // 페이지뷰 기반 점수 (30%)
      if (sessionData.pageViews > 5) qualityScore += 30
      else if (sessionData.pageViews > 3) qualityScore += 25
      else if (sessionData.pageViews > 1) qualityScore += 20
      else qualityScore += 5

      // 상호작용 기반 점수 (30%)
      if (interactionRate > 1)
        qualityScore += 30 // 초당 1회 이상
      else if (interactionRate > 0.5) qualityScore += 25
      else if (interactionRate > 0.1) qualityScore += 20
      else qualityScore += 5

      return Math.min(qualityScore, 100)
    }

    const trackSessionEnd = () => {
      const sessionDuration = Date.now() - sessionData.startTime
      const qualityScore = calculateSessionQuality()

      trackEvent('session_end', {
        event_category: 'session',
        session_duration: sessionDuration,
        page_views: sessionData.pageViews,
        total_interactions: sessionData.interactions,
        bounce: sessionDuration < sessionData.bounceThreshold,
        quality_score: qualityScore,
        engagement_level:
          qualityScore > 80 ? 'high' : qualityScore > 50 ? 'medium' : 'low',
        avg_time_per_page:
          sessionData.pageViews > 0
            ? Math.round(sessionDuration / sessionData.pageViews)
            : 0,
        interaction_rate:
          sessionDuration > 0
            ? Math.round(
                (sessionData.interactions / (sessionDuration / 1000)) * 100,
              ) / 100
            : 0,
        um_idx: app.user.UM_IDX ?? '',
        timestamp: new Date().toISOString(),
      })
    }

    // 페이지 뷰 카운트
    router.afterEach(() => {
      sessionData.pageViews++
    })

    // 상호작용 카운트
    onMounted(() => {
      const interactionEvents = ['click', 'keydown', 'scroll', 'touchstart']

      const handleInteraction = () => {
        sessionData.interactions++
      }

      interactionEvents.forEach((eventType) => {
        document.addEventListener(eventType, handleInteraction, {
          passive: true,
        })
      })

      // 세션 종료 시 추적
      window.addEventListener('beforeunload', trackSessionEnd)

      return () => {
        interactionEvents.forEach((eventType) => {
          document.removeEventListener(eventType, handleInteraction)
        })
        window.removeEventListener('beforeunload', trackSessionEnd)
      }
    })

    return { sessionData, calculateSessionQuality, trackSessionEnd }
  }

  return {
    trackEvent,
    trackButtonClick,
    trackPageView,
    setupAutoTracking,
    setupPageTimeTracking,
    setupScrollTracking,
    setupSessionAnalytics,
  }
}
